import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';

import { NotificationCenter, NotificationCenterContext } from '@bloomays-lib/ui.shared';

import { useBreadcrumbStore } from '../../stores';
import { formatMonthYearFr, isDate } from '@bloomays-lib/utils.shared';

type Crumb = {
  label: string;
  to?: string;
};

export type BreadcrumbProps = {
  /** Affiche le bouton d'ouverture du menu (mobile). */
  onMenuClick?: () => void;
  /** Contexte du centre de notifications : affiche la cloche à droite. */
  notificationCenterContext?: NotificationCenterContext;
};

// Détecte les segments dynamiques (ids Airtable, uuid, nombres, tokens longs…).
const isDynamicSegment = (segment: string): boolean => {
  return (
    /^rec[A-Za-z0-9]{8,}$/.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(segment) ||
    /^\d+$/.test(segment) ||
    segment.length > 24
  );
};

const Breadcrumb = ({ onMenuClick, notificationCenterContext }: BreadcrumbProps) => {
  const { t } = useTranslation('menu');
  const location = useLocation();
  const navigate = useNavigate();
  // Libellés dynamiques fournis par les pages (ex: nom de la mission) — prioritaires sur les libellés génériques.
  const dynamicLabels = useBreadcrumbStore((state) => state.labels);

  const crumbs = useMemo<Crumb[]>(() => {
    // Libellés lisibles par segment d'URL connu.
    const labels: Record<string, string> = {
      dashboard: t('dashboard'),
      'missions-freelance': t('freelanceMissions'),
      planningMission: t('planningMission'),
      billingsActivity: t('billingsActivity'),
      finance: t('finance'),
      talents: t('talents'),
      talentsV2: t('talents'),
      activities: t('activities'),
      contracts: t('legalContracts'),
      jobs: t('jobs'),
      bdc: t('purchaseOrders'),
      primes: t('primes'),
      mois: t('breadcrumb.monthly'),
      annuel: t('breadcrumb.annual'),
      missions: t('breadcrumb.myMissions'),
      settings: t('settings'),
      me: t('breadcrumb.me'),
      informations: t('meInformations'),
      society: t('breadcrumb.society'),
      mission: t('missions'),
      admin: t('sections.admin'),
    };

    const segments = location.pathname.split('/').filter(Boolean);
    const result: Crumb[] = [];
    let cumulative = '';

    segments.forEach((segment, index) => {
      cumulative += `/${segment}`;
      const isLast = index === segments.length - 1;
      const previousSegment = index > 0 ? segments[index - 1] : undefined;

      let label: string;
      if (dynamicLabels[cumulative]) {
        // Libellé fourni par la page courante (ex: nom de la mission au lieu de "Détail").
        label = dynamicLabels[cumulative];
      } else if (segment === 'dashboard' && previousSegment === 'primes') {
        label = t('breadcrumb.tamSynthesis');
      } else if (labels[segment]) {
        label = labels[segment];
      } else if (isDynamicSegment(segment)) {
        // Le détail d'une mission Bloomer (/mission/:recordId) sert à gérer les CRA.
        label = previousSegment === 'mission' ? t('breadcrumb.manageCra') : t('breadcrumb.detail');
      } else {
        if (isDate(segment)) {
          label = formatMonthYearFr(segment) || 'Date de CRA';
        } else {
          label = segment.charAt(0).toUpperCase() + segment.slice(1);
        }
      }

      result.push({ label, to: isLast ? undefined : cumulative });
    });

    return result;
  }, [location.pathname, t, dynamicLabels]);

  return (
    <Bar>
      {onMenuClick && (
        <MenuToggle type="button" aria-label={t('openMenu')} onClick={onMenuClick}>
          <MenuOutlinedIcon />
        </MenuToggle>
      )}
      <Trail aria-label="fil d'Ariane">
        <HomeLink type="button" aria-label={t('home')} onClick={() => void navigate('/')}>
          <HomeOutlinedIcon fontSize="small" />
        </HomeLink>
        {crumbs.map((crumb, index) => (
          <Fragment key={`${crumb.label}-${index}`}>
            <Separator>
              <ChevronRightIcon fontSize="small" />
            </Separator>
            {crumb.to ? (
              <CrumbLink type="button" onClick={() => crumb.to && void navigate(crumb.to)}>
                {crumb.label}
              </CrumbLink>
            ) : (
              <CurrentCrumb variant="body2SemiBold">{crumb.label}</CurrentCrumb>
            )}
          </Fragment>
        ))}
      </Trail>
      <Spacer />
      {notificationCenterContext && (
        <Actions>
          <NotificationCenter
            initNotifications={notificationCenterContext.notifications || []}
            onRead={notificationCenterContext.notificationsRead}
            onReadAll={notificationCenterContext.notificationsReadAll}
            onClearAll={notificationCenterContext.notificationsDeleteAll}
          />
        </Actions>
      )}
    </Bar>
  );
};

export default Breadcrumb;
export { Breadcrumb };

const Bar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '14px 24px',
  minHeight: '56px',
  backgroundColor: theme.palette.neutral.white,
  borderBottom: `1px solid ${theme.palette.neutral[20]}`,
  [theme.breakpoints.down('md')]: {
    padding: '10px 16px',
  },
}));

const MenuToggle = styled('button')(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  flexShrink: 0,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.neutral[20]}`,
  background: theme.palette.neutral.white,
  color: theme.palette.neutral[70],
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
  '&:hover': {
    backgroundColor: theme.palette.blue[20],
    color: theme.palette.blue[80],
  },
}));

const Trail = styled('nav')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '2px',
  minWidth: 0,
}));

const HomeLink = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  border: 'none',
  background: 'transparent',
  color: theme.palette.neutral[50],
  cursor: 'pointer',
  borderRadius: '6px',
  '&:hover': {
    color: theme.palette.blue[80],
    backgroundColor: theme.palette.blue[20],
  },
}));

const Separator = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.neutral[30],
  svg: { fontSize: '18px' },
}));

const CrumbLink = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '4px 6px',
  borderRadius: '6px',
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '13px',
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
  '&:hover': {
    color: theme.palette.blue[80],
    backgroundColor: theme.palette.blue[20],
  },
}));

const CurrentCrumb = styled(Typography)(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '13px',
  color: theme.palette.neutral[90],
  padding: '4px 6px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Spacer = styled('div')(() => ({
  flex: 1,
  minWidth: '8px',
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
}));
